import type { SessionInvoiceParams } from "@/invoice/session-invoice"

import type { Invoice } from "@be-miniapp/api"

interface StoredSessionInvoice {
  userId: number
  paramsKey: string
  invoice: Invoice
  createdAt: string
}

const STORAGE_KEY = "be_session_invoices"

function getSessionInvoiceParamsKey(params: SessionInvoiceParams): string {
  return JSON.stringify({
    company_name: params.company_name,
    company_tax_no: params.company_tax_no,
    company_address: params.company_address,
    email_receiver: params.email_receiver,
  })
}

const getStorageData = (): StoredSessionInvoice[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch (error) {
    console.warn("Failed to parse session invoice storage:", error)
    return []
  }
}

const writeStorageData = (data: StoredSessionInvoice[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch (error) {
    console.error("Failed to store session invoice:", error)
  }
}

/**
 * Saves session invoice to localStorage keyed by user + launch invoice params.
 * Reused when the partner reopens the app with the same invoice info.
 */
export function storeSessionInvoice(
  userId: number,
  params: SessionInvoiceParams,
  invoice: Invoice
): void {
  const paramsKey = getSessionInvoiceParamsKey(params)
  const existingData = getStorageData()

  const filteredData = existingData.filter(
    (item) => !(item.userId === userId && item.paramsKey === paramsKey)
  )

  filteredData.push({
    userId,
    paramsKey,
    invoice,
    createdAt: new Date().toISOString(),
  })

  writeStorageData(filteredData)
}

/**
 * Retrieves cached session invoice for user + launch params.
 */
export function getStoredSessionInvoice(
  userId: number,
  params: SessionInvoiceParams
): Invoice | null {
  try {
    const paramsKey = getSessionInvoiceParamsKey(params)
    const found = getStorageData().find(
      (item) => item.userId === userId && item.paramsKey === paramsKey
    )

    return found?.invoice ?? null
  } catch (error) {
    console.error("Failed to retrieve session invoice:", error)
    return null
  }
}

/**
 * Clears cached session invoice for user + launch params.
 */
export function clearStoredSessionInvoice(
  userId: number,
  params: SessionInvoiceParams
): void {
  try {
    const paramsKey = getSessionInvoiceParamsKey(params)
    const filteredData = getStorageData().filter(
      (item) => !(item.userId === userId && item.paramsKey === paramsKey)
    )

    writeStorageData(filteredData)
  } catch (error) {
    console.error("Failed to clear session invoice:", error)
  }
}

/**
 * Clears all cached session invoices for a user.
 */
export function clearUserSessionInvoices(userId: number): void {
  try {
    const filteredData = getStorageData().filter(
      (item) => item.userId !== userId
    )
    writeStorageData(filteredData)
  } catch (error) {
    console.error("Failed to clear user session invoices:", error)
  }
}

export function getAllStoredSessionInvoices(): StoredSessionInvoice[] {
  return getStorageData()
}

/** @internal Test helper */
export function resetSessionInvoiceStorageForTests(): void {
  localStorage.removeItem(STORAGE_KEY)
}
